import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import config from '../config/config';
import Skeleton from './common/Skeleton';
import EmptyState from './common/EmptyState';
import { useToast } from './common/ToastContext';
import './CommandCenter.css';

/* ── Color constants ────────────────────────────────────────────────────────── */
const SEVERITY_COLORS = { CRITICAL: '#d32f2f', HIGH: '#f57c00', MEDIUM: '#fbc02d', LOW: '#1976d2' };
const GRADE_COLORS = { A: '#4caf50', B: '#8bc34a', C: '#ff9800', D: '#f57c00', F: '#d32f2f' };
const SUPPLIER_COLORS = { trusted: '#4caf50', moderate: '#ff9800', low: '#f57c00', untrusted: '#d32f2f' };
const REMEDIATION_COLORS = ['#1976d2', '#d32f2f', '#ff9800', '#7c4dff', '#4caf50'];

function trustColor(score) {
  if (score >= 80) return '#4caf50';
  if (score >= 60) return '#ff9800';
  return '#f44336';
}

function riskHeatColor(score) {
  if (score >= 80) return '#4caf50';
  if (score >= 60) return '#8bc34a';
  if (score >= 40) return '#ff9800';
  return '#f44336';
}

function complianceColor(pct) {
  if (pct > 70) return '#4caf50';
  if (pct > 40) return '#ff9800';
  return '#f44336';
}

/* ── Animated number (counts up) ────────────────────────────────────────────── */
function AnimatedNumber({ value, duration = 1200, decimals = 0 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (value == null) return;
    const target = Number(value);
    const start = performance.now();
    let raf;
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(eased * target);
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{decimals > 0 ? display.toFixed(decimals) : Math.round(display).toLocaleString()}</>;
}

/* ── Trust Score Gauge (SVG donut) ──────────────────────────────────────────── */
function TrustGauge({ score, label }) {
  const [animScore, setAnimScore] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setAnimScore(score), 100);
    return () => clearTimeout(timer);
  }, [score]);

  const radius = 80;
  const stroke = 12;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(animScore / 100, 1);
  const offset = circumference * (1 - pct);
  const color = trustColor(score);

  return (
    <div className="cc-gauge">
      <svg viewBox="0 0 200 200" className="cc-gauge-svg">
        <circle cx="100" cy="100" r={radius} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={stroke} />
        <circle
          cx="100" cy="100" r={radius} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round"
          className="cc-gauge-arc"
          transform="rotate(-90 100 100)"
        />
      </svg>
      <div className="cc-gauge-inner">
        <span className="cc-gauge-value" style={{ color }}><AnimatedNumber value={score} decimals={1} /></span>
        <span className="cc-gauge-label">{label}</span>
      </div>
    </div>
  );
}

/* ── Mini Gauge (sub-scores) ─────────────────────────────────────────────────── */
function MiniGauge({ score, label, size = 60 }) {
  const [animScore, setAnimScore] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setAnimScore(score), 200);
    return () => clearTimeout(timer);
  }, [score]);

  const radius = size * 0.38;
  const stroke = size * 0.06;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(animScore / 100, 1);
  const offset = circumference * (1 - pct);
  const color = trustColor(score);
  const center = size / 2;

  return (
    <div className="cc-mini-gauge">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={stroke} />
        <circle
          cx={center} cy={center} r={radius} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round"
          className="cc-gauge-arc"
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <div className="cc-mini-gauge-inner">
        <span className="cc-mini-gauge-value" style={{ color }}>{Math.round(score)}</span>
      </div>
      <div className="cc-mini-gauge-label">{label}</div>
    </div>
  );
}

/* ── Stat Card ──────────────────────────────────────────────────────────────── */
function StatCard({ icon, label, value, sub, color, delay = 0 }) {
  return (
    <div className="ce-kpi cc-fade-in" style={{ animationDelay: `${delay}ms`, '--kpi-color': color || 'var(--accent)' }}>
      <div className="ce-kpi-value" style={color ? { color } : undefined}><AnimatedNumber value={value} /></div>
      <div className="ce-kpi-label">{icon} {label}</div>
      {sub && <div style={{ fontSize: '0.76rem', color: 'var(--bs-secondary-color)' }}>{sub}</div>}
    </div>
  );
}

/* ── Section wrapper ────────────────────────────────────────────────────────── */
function Section({ title, className = '', children, delay = 0, action }) {
  return (
    <div className={`ce-card mb-4 cc-fade-in ${className}`} style={{ animationDelay: `${delay}ms` }}>
      <div className="ce-card-header ce-section-header">
        <span className="ce-card-title ce-section-title">{title}</span>
        {action && <Link to={action.to} className="ce-btn ce-btn--secondary ce-btn--sm">{action.label}</Link>}
      </div>
      <div className="ce-card-body">
        {children}
      </div>
    </div>
  );
}

/* ── Compliance bar ─────────────────────────────────────────────────────────── */
function ComplianceBar({ label, value, total }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  const color = complianceColor(pct);
  return (
    <div className="cc-compliance-row">
      <div className="cc-compliance-label">{label}</div>
      <div className="cc-compliance-bar-track">
        <div className="cc-compliance-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="cc-compliance-count" style={{ color }}>{value} / {total} <span className="cc-compliance-pct">({pct.toFixed(0)}%)</span></div>
    </div>
  );
}

/* ── Supply chain metric card ───────────────────────────────────────────────── */
function SupplyMetric({ icon, label, value, alert, sub }) {
  return (
    <div className={`cc-supply-metric${alert ? ' cc-supply-alert' : ''}`}>
      <div className="cc-supply-icon">{icon}</div>
      <div className="cc-supply-value">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      <div className="cc-supply-label">{label}</div>
      {sub && <div className="cc-supply-sub">{sub}</div>}
    </div>
  );
}

/* ── Custom tooltip for Recharts ────────────────────────────────────────────── */
function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="cc-tooltip">
      <span className="cc-tooltip-dot" style={{ background: d.payload?.fill || d.color }} />
      <span>{d.name}: <strong>{d.value?.toLocaleString()}</strong></span>
    </div>
  );
}

/* ── Heatmap tooltip ────────────────────────────────────────────────────────── */
function HeatmapCell({ repo }) {
  const [hover, setHover] = useState(false);
  const color = riskHeatColor(repo.riskScore);
  const size = Math.max(24, Math.min(48, 20 + (repo.packages || 0) / 20));
  return (
    <div
      className="cc-heat-cell"
      style={{ width: size, height: size, background: color }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {hover && (
        <div className="cc-heat-tooltip">
          <strong>{repo.name}</strong>
          <div>Risk: {repo.riskScore?.toFixed(1)}</div>
          <div>Critical: {repo.critical}</div>
          <div>Packages: {repo.packages}</div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════ */
/* ═══ Main Component ════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════════════════════ */

export default function CommandCenter() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${config.BASE_URL}/command-center`);
      setData(res.data);
    } catch (err) {
      setError(err.message || 'Failed to load');
      showToast(t('commandCenter.loadError') || 'Failed to load Command Center', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── Derived chart data ──────────────────────────────────────────────────── */
  const severityData = useMemo(() => {
    if (!data?.distributions?.severity) return [];
    return Object.entries(data.distributions.severity).map(([key, val]) => ({
      name: key, value: val, fill: SEVERITY_COLORS[key] || '#999',
    }));
  }, [data]);

  const remediationData = useMemo(() => {
    if (!data?.remediation) return [];
    const r = data.remediation;
    return [
      { name: t('commandCenter.tracked'), value: r.tracked },
      { name: t('commandCenter.overdue'), value: r.overdue },
      { name: t('commandCenter.acceptedRisk'), value: r.acceptedRisk },
      { name: t('commandCenter.reachable'), value: r.reachable },
      { name: t('commandCenter.notAffected'), value: r.notAffected },
    ];
  }, [data, t]);

  const supplierData = useMemo(() => {
    if (!data?.supplyChain?.supplierTiers) return [];
    return Object.entries(data.supplyChain.supplierTiers).map(([key, val]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1), value: val, fill: SUPPLIER_COLORS[key] || '#999',
    }));
  }, [data]);

  const qualityData = useMemo(() => {
    if (!data?.distributions?.qualityGrade) return [];
    return Object.entries(data.distributions.qualityGrade).map(([key, val]) => ({
      name: key, value: val, fill: GRADE_COLORS[key] || '#999',
    }));
  }, [data]);

  /* ── Loading state ───────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="cc-page">
        <div className="cc-hero cc-fade-in">
          <div className="cc-hero-content">
            <Skeleton width={200} height={200} borderRadius={100} />
          </div>
        </div>
        <div className="cc-grid">
          <Skeleton.Card /><Skeleton.Card /><Skeleton.Card /><Skeleton.Card />
        </div>
      </div>
    );
  }

  /* ── Error state ─────────────────────────────────────────────────────────── */
  if (error || !data) {
    return (
      <div className="cc-page">
        <EmptyState
          icon="🎯"
          title={t('commandCenter.noData')}
          description={t('commandCenter.noDataDesc')}
          action={{ label: t('dashboard.retry') || 'Retry', onClick: fetchData }}
        />
      </div>
    );
  }

  const totalSeverity = (data.severity?.critical || 0) + (data.severity?.high || 0) + (data.severity?.medium || 0) + (data.severity?.low || 0);

  return (
    <div className="cc-page">
      {/* ── 1. HERO ──────────────────────────────────────────────────────────── */}
      <div className="cc-hero cc-fade-in">
        <div className="cc-hero-content">
          <div className="cc-hero-stats cc-hero-stats-left">
            <StatCard icon="🗂️" label={t('commandCenter.totalRepos')} value={data.totalRepos} delay={100} />
            <StatCard icon="📦" label={t('commandCenter.totalPackages')} value={data.totalPackages} delay={200} />
            <StatCard icon="🛡️" label={t('commandCenter.totalVulnerabilities')} value={data.totalVulnerabilities} color="#d32f2f" delay={300} />
          </div>
          <div className="cc-hero-center">
            <TrustGauge score={data.portfolioTrustScore || 0} label={t('commandCenter.portfolioTrustScore')} />
            <div className="cc-sub-scores">
              <MiniGauge score={data.subScores?.sca ?? 0} label={t('commandCenter.scaScore')} />
              <MiniGauge score={data.subScores?.sast ?? 0} label={t('commandCenter.sastScore')} />
              <MiniGauge score={data.subScores?.supplyChain ?? 0} label={t('commandCenter.supplyChainScore')} />
            </div>
          </div>
          <div className="cc-hero-stats cc-hero-stats-right">
            <StatCard icon="📊" label={t('commandCenter.avgQuality')} value={data.avgQualityScore} sub={`/ 100`} delay={100} />
            <StatCard icon="🇪🇺" label={t('commandCenter.avgCRA')} value={data.avgCRAScore} sub={`/ 100`} delay={200} />
            <StatCard
              icon={data.agent?.status?.enabled ? '🟢' : '🔴'}
              label={t('commandCenter.agentStatus')}
              value={data.agent?.status?.enabled ? 'Active' : 'Disabled'}
              sub={data.agent?.status?.schedule}
              delay={300}
            />
          </div>
        </div>
      </div>

      {/* ── 2 + 3. THREAT LANDSCAPE + COMPLIANCE ─────────────────────────────── */}
      <div className="cc-grid cc-grid-60-40">
        {/* Threat Landscape */}
        <div className="cc-col">
          <Section title={t('commandCenter.severityBreakdown')} delay={400}>
            <div className="cc-severity-row">
              <div className="cc-severity-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={severityData} dataKey="value" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2} stroke="none">
                      {severityData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="cc-severity-legend">
                {severityData.map(s => (
                  <div key={s.name} className="cc-severity-item">
                    <span className="cc-severity-dot" style={{ background: s.fill }} />
                    <span className="cc-severity-name">{s.name}</span>
                    <span className="cc-severity-count">{s.value.toLocaleString()}</span>
                    <span className="cc-severity-pct">{totalSeverity > 0 ? ((s.value / totalSeverity) * 100).toFixed(1) : 0}%</span>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          <Section title={t('commandCenter.topRisks')} delay={500}>
            <div className="ce-table-wrap">
              <table className="ce-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Repository</th>
                    <th>Risk Score</th>
                    <th>Critical</th>
                    <th>High</th>
                    <th>Total</th>
                    <th>Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.topRisks || []).slice(0, 10).map((repo, i) => {
                    const riskPct = Math.min(100, (repo.riskScore / 20) * 100);
                    return (
                      <tr key={repo.name}>
                        <td style={{ fontWeight: 600, color: 'var(--bs-secondary-color)' }}>{i + 1}</td>
                        <td>
                          <Link to={`/RepoDetails/${encodeURIComponent(repo.name)}`} style={{ color: 'var(--accent)', fontWeight: 500 }}>{repo.name}</Link>
                        </td>
                        <td>
                          <div className="cc-risk-bar-cell">
                            <div className="cc-risk-bar-track">
                              <div className="cc-risk-bar-fill" style={{ width: `${riskPct}%`, background: riskPct > 60 ? '#d32f2f' : riskPct > 30 ? '#f57c00' : '#4caf50' }} />
                            </div>
                            <span className="cc-risk-bar-label">{repo.riskScore?.toFixed(1)}</span>
                          </div>
                        </td>
                        <td><span className="ce-sev ce-sev--critical">{repo.criticalVulns}</span></td>
                        <td><span className="ce-sev ce-sev--high">{repo.highVulns}</span></td>
                        <td>{repo.totalVulns}</td>
                        <td><span className="ce-tag" style={{ background: (GRADE_COLORS[repo.qualityGrade] || '#999') + '22', color: GRADE_COLORS[repo.qualityGrade] || '#999', border: `1px solid ${GRADE_COLORS[repo.qualityGrade] || '#999'}55` }}>{repo.qualityGrade}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        </div>

        {/* Compliance Posture */}
        <div className="cc-col">
          <Section title={t('commandCenter.compliancePosture')} delay={450}>
            <div className="cc-compliance-list">
              <ComplianceBar label={t('commandCenter.ntiaCompliant')} value={data.compliance?.ntiaCompliant || 0} total={data.compliance?.totalRepos || 0} />
              <ComplianceBar label={t('commandCenter.craReady')} value={data.compliance?.craReady || 0} total={data.compliance?.totalRepos || 0} />
              <ComplianceBar label={t('commandCenter.policyPassing')} value={data.compliance?.policyPassing || 0} total={data.compliance?.totalRepos || 0} />
              <ComplianceBar label={t('commandCenter.slsaVerified')} value={data.compliance?.slsaVerified || 0} total={data.compliance?.totalRepos || 0} />
              <ComplianceBar label={t('commandCenter.reachScanned')} value={data.compliance?.reachScanned || 0} total={data.compliance?.totalRepos || 0} />
            </div>
          </Section>

          <Section title="Quality Distribution" delay={550}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={qualityData} barSize={36}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="name" tick={{ fill: 'var(--cc-text-secondary)', fontSize: 13 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--cc-text-secondary)', fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {qualityData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Section>
        </div>
      </div>

      {/* ── 4. SUPPLY CHAIN HEALTH ───────────────────────────────────────────── */}
      <Section title={t('commandCenter.supplyChainHealth')} className="cc-full-width" delay={600}>
        <div className="cc-supply-grid">
          <SupplyMetric icon="🦠" label={t('commandCenter.malwareFindings')} value={data.supplyChain?.malwareFindings || 0} alert={(data.supplyChain?.malwareFindings || 0) > 0} />
          <SupplyMetric icon="🎭" label={t('commandCenter.typosquats')} value={data.supplyChain?.typosquats || 0} alert={(data.supplyChain?.typosquats || 0) > 0} />
          <SupplyMetric icon="⚠️" label={t('commandCenter.anomalies')} value={data.supplyChain?.anomalies || 0} sub={`${data.supplyChain?.highAnomalies || 0} high severity`} alert={(data.supplyChain?.highAnomalies || 0) > 0} />
          <SupplyMetric icon="📦" label={t('commandCenter.abandonedPkgs')} value={data.supplyChain?.abandonedPkgs || 0} />
        </div>
        <div className="cc-supplier-bar-section">
          <div className="cc-supplier-bar-label">{t('commandCenter.supplierTrust')}</div>
          <div className="cc-supplier-bar-track">
            {supplierData.map(s => {
              const total = supplierData.reduce((a, b) => a + b.value, 0);
              const pct = total > 0 ? (s.value / total) * 100 : 0;
              return pct > 0 ? (
                <div key={s.name} className="cc-supplier-bar-seg" style={{ width: `${pct}%`, background: s.fill }} title={`${s.name}: ${s.value}`}>
                  <span className="cc-supplier-bar-text">{s.name} ({s.value})</span>
                </div>
              ) : null;
            })}
          </div>
        </div>
      </Section>

      {/* ── 4b. CODE SECURITY (Vigil SAST) ────────────────────────────────────── */}
      <Section title={t('commandCenter.codeSecurity')} className="cc-full-width" delay={650}>
        <div className="cc-supply-grid">
          <SupplyMetric icon="🔍" label={t('commandCenter.sastFindings')} value={data.codeSecurity?.totalFindings || 0} alert={(data.codeSecurity?.critical || 0) > 0} />
          <SupplyMetric icon="🔴" label={t('commandCenter.sastCritical')} value={data.codeSecurity?.critical || 0} alert={(data.codeSecurity?.critical || 0) > 0} />
          <SupplyMetric icon="🟠" label={t('commandCenter.sastHigh')} value={data.codeSecurity?.high || 0} alert={(data.codeSecurity?.high || 0) > 0} />
          <SupplyMetric icon="📂" label={t('commandCenter.sastScanned')} value={data.codeSecurity?.reposScanned || 0} sub={`of ${data.codeSecurity?.totalRepos || 0} repos`} />
        </div>
        {(data.codeSecurity?.reposScanned || 0) === 0 && (
          <div className="cc-sast-empty">
            <span>No Vigil SAST scans yet — </span>
            <a href="/vigil/scan">run a scan →</a>
          </div>
        )}
      </Section>

      {/* ── 5 + 6. REMEDIATION + HEATMAP ──────────────────────────────────────── */}
      <div className="cc-grid cc-grid-50-50">
        {/* Remediation Pipeline */}
        <Section title={t('commandCenter.remediationPipeline')} delay={700} action={{ to: '/repos', label: t('commandCenter.viewAll') }}>
          <div className="cc-remediation-row">
            <div className="cc-remediation-chart">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={remediationData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} stroke="none">
                    {remediationData.map((_, i) => <Cell key={i} fill={REMEDIATION_COLORS[i]} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="cc-remediation-legend">
              {remediationData.map((item, i) => (
                <div key={item.name} className="cc-remediation-item">
                  <span className="cc-severity-dot" style={{ background: REMEDIATION_COLORS[i] }} />
                  <span className="cc-remediation-name">{item.name}</span>
                  <span className="cc-remediation-count" style={{ color: REMEDIATION_COLORS[i] }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Risk Heatmap */}
        <Section title={t('commandCenter.riskHeatmap')} delay={800}>
          <div className="cc-heatmap-grid">
            {(data.heatmap || []).map((repo, i) => (
              <HeatmapCell key={repo.name || i} repo={repo} />
            ))}
          </div>
          <div className="cc-heatmap-legend">
            <span className="cc-heat-legend-item"><span className="cc-heat-swatch" style={{ background: '#4caf50' }} />80+</span>
            <span className="cc-heat-legend-item"><span className="cc-heat-swatch" style={{ background: '#8bc34a' }} />60–79</span>
            <span className="cc-heat-legend-item"><span className="cc-heat-swatch" style={{ background: '#ff9800' }} />40–59</span>
            <span className="cc-heat-legend-item"><span className="cc-heat-swatch" style={{ background: '#f44336' }} />&lt;40</span>
          </div>
        </Section>
      </div>

      {/* ── 7. AGENT STATUS BAR ──────────────────────────────────────────────── */}
      <div className="cc-agent-bar cc-fade-in" style={{ animationDelay: '900ms' }}>
        <div className="cc-agent-status">
          <span className={`cc-agent-dot${data.agent?.status?.enabled ? ' cc-agent-active' : ''}`} />
          <span className="cc-agent-status-text">
            {data.agent?.status?.enabled ? 'Agent Active' : 'Agent Disabled'}
          </span>
        </div>
        <div className="cc-agent-detail">
          <span className="cc-agent-meta">
            {t('commandCenter.lastRun')}: {data.agent?.status?.lastRunDuration || '—'} — {data.agent?.status?.lastRunCVEs?.toLocaleString() || 0} CVEs scanned, {data.agent?.status?.lastRunActions?.toLocaleString() || 0} actions
          </span>
        </div>
        <div className="cc-agent-actions">
          <Link to="/security/agent" className="cc-agent-link">
            {data.agent?.pendingActions || 0} {t('commandCenter.pendingActions')}
          </Link>
          <span className="cc-agent-schedule">{data.agent?.status?.schedule || 'Manual'}</span>
        </div>
      </div>
    </div>
  );
}
